// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricConfig, BaseMetricEntity, ChartType, ScoreLevel } from './base-metric.entity';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse
} from './metric-chart-base.entity';
import { formatDataSources } from './metric-column-formatters.utility';

/**
 * Configuration interface for CommitActivities entity
 */
type CommitActivitiesConfig = BaseMetricConfig;

/**
 * Commit activity data model from SQL query (API returns uppercase keys)
 */
export interface CommitActivityData {
  ACTIVITY_TS: string;
  REPOSITORY_URL: string;
  PROJECT_SLUG: string;
  MEMBER_ID: string;
  MEMBER_DISPLAY_NAME: string;
  USER_PHOTO_URL: string;
  COMMIT_CONTENT: string;
  COMMIT_URL: string;
}

/**
 * Commit activities summary data model from SQL query
 */
export interface CommitActivitiesSummaryData {
  totalCommits: number;
  dataSources: string;
}

/**
 * CommitActivitiesEntity - Domain entity for commit activities business rules
 *
 * This entity encapsulates the business logic for calculating and determining
 * commit activities influence levels based on percentage of commits by company employees.
 *
 * Business Rules:
 * - 0.0-0.9% of commit activities = Silent (0 points) - Red color, (!) icon
 * - 1.0-4.9% of commit activities = Participating (1 point) - Yellow color, line in circle icon
 * - 5.0-19.9% of commit activities = Contributing (2 points) - Blue color, thumbs up icon
 * - > 20% of commit activities = Leading (3 points) - Green color, thumbs up icon
 *
 * Handles SQL query: analytics.platinum_organization_dashboard.code_commits_by_project
 */
@Injectable({
  providedIn: 'root'
})
export class CommitActivities extends BaseMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);
  static readonly METRIC_TYPE = 'commit_activities' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: CommitActivitiesConfig = {
    metricType: 'commit_activities',
    display: {
      displayName: 'Commit Activities',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'What percentage of commit activities are from our company?',
        scoringRules: [
          'Each 0.1% of commit activities to the project total = 0.1 point, and any number above 0 gets a +1 bonus',
          '0 commit activities = 0 points',
          '0.000001 - 0.1% of commit activities = 1.1 points',
          '0.2% of commit activities = 1.2 points',
          '2.5% of commit activities = 3.5 points',
          '20.0% of commit activities = 21.0 points',
          '0 points = <b>Silent</b>',
          '1 point = <b>Participating</b>',
          '2-9 points = <b>Contributing</b>',
          '10+ points = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: 'Our company has no commit activities to this project.',
      },
      positiveValue: {
        descriptionTemplate: 'Employees from our company made {count}% of all commit activities to this project.',
      }
    }
  };

  static getMetricConfig() {
    return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for commit activities
   */
  static getBottomSummary(value?: string, level?: ScoreLevel): MetricBottomSummary {
    const commitPercentage = parseFloat(value || '0.0');
    const styles = level ? this.getStylesByLevel(level) : this.getNullValueStyles();
    const bottomSummary = this.CONFIG.bottomSummary;

    if (commitPercentage === 0) {
      return {
        title: this.CONFIG.display.displayName,
        description: bottomSummary.zeroValue.description,
        icon: styles.icon,
        iconColor: styles.iconColor,
        borderColor: styles.borderColor
      };
    }

    const description = this.formatDescription(
      bottomSummary.positiveValue.descriptionTemplate,
      {
        count: value || '0.0'
      }
    );

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }

  /**
   * Get columns for commit activities table
   */
  getColumns(): Column[] {
    return [
      { name: 'Repository Group', prop: 'repositoryGroup' },
      { name: 'Committer', prop: 'committer', clickableText: true },
      { name: 'Date', prop: 'date' },
      { name: 'Commit', prop: 'commit', clickableText: true }
    ];
  }

  /**
   * Get API endpoint for commit activities
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/commit-activities`;
  }

  /**
   * Fetch commit activities data from backend
   * Executes SQL: SELECT activity_ts, repository_url, project_slug, member_id, member_display_name,
   * user_photo_url, commit_content, commit_url
   * FROM analytics.platinum_organization_dashboard.code_commits_by_project
   * WHERE account_id='{account_id}' AND project_slug='{project_slug}'
   * AND IS_COMMIT_LAST_12MO = TRUE
   * ORDER BY ACTIVITY_TS DESC
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<MetricChartApiResponse<CommitActivityData>>(
          this.getEndpoint(),
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch commit activities data');
            }

            return response.data.map(commit => this.transformToDataRow(commit));
          }),
          catchError(error => {
            console.error('Error fetching commit activities data:', error);
            return of([]);
          })
        );
      }),
      catchError(error => {
        console.error('Error in commit activities entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform commit activity data to DataRow format for the table
   */
  private transformToDataRow(commit: CommitActivityData): DataRow {
    const committerWithImage = this.formatCommitterWithImage(
      commit.MEMBER_DISPLAY_NAME,
      commit.USER_PHOTO_URL
    );

    const commitWithLink = this.formatCommitWithLink(
      commit.COMMIT_CONTENT,
      commit.COMMIT_URL
    );

    return {
      repositoryGroup: { text: this.extractRepositoryName(commit.REPOSITORY_URL) },
      committer: {
        ...committerWithImage,
        extraData: {
          memberId: commit.MEMBER_ID,
          memberDisplayName: commit.MEMBER_DISPLAY_NAME,
          memberLogoUrl: commit.USER_PHOTO_URL
        }
      },
      date: { text: this.formatDateTime(commit.ACTIVITY_TS) },
      commit: commitWithLink
    };
  }

  /**
   * Format committer name with circular image
   */
  private formatCommitterWithImage(
    displayName: string,
    photoUrl: string
  ): { text: string; isHtml: boolean } {
    const imageHtml = photoUrl
      ? `<img src="${photoUrl}" alt="${displayName}" class="maintainer-avatar" onerror="this.style.display='none'">`
      : '';

    return {
      text: `<div class="maintainer-profile">${imageHtml}<span class="maintainer-name">${displayName}</span></div>`,
      isHtml: true
    };
  }

  /**
   * Format commit content with clickable link
   */
  private formatCommitWithLink(
    commitContent: string,
    commitUrl: string
  ): { text: string; isHtml: boolean; url?: string } {
    const truncatedContent = this.truncateText(commitContent, 80);

    return {
      text: `<a href="${commitUrl}" target="_blank" rel="noopener noreferrer">${truncatedContent}</a>`,
      isHtml: true,
      url: commitUrl
    };
  }

  /**
   * Extract repository name from URL
   */
  private extractRepositoryName(repositoryUrl: string): string {
    try {
      const parts = repositoryUrl.split('/');
      return parts[parts.length - 1] || repositoryUrl;
    } catch {
      return repositoryUrl;
    }
  }

  /**
   * Truncate text to specified length
   */
  private truncateText(text: string, maxLength: number): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  /**
   * Format date string for display with format MMM D, YYYY h:m:s
   */
  private formatDateTime(dateString: string): string {
    try {
      const date = new Date(dateString);
      const formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      const formattedTime = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      return `${formattedDate} ${formattedTime}`;
    } catch {
      return dateString; // Return original if parsing fails
    }
  }

  /**
   * Fetch commit activities summary data from backend
   * Executes SQL: SELECT COUNT(DISTINCT commit_id) total_commits,
   * LISTAGG(DISTINCT platform, ', ') data_sources
   * FROM analytics.platinum_organization_dashboard.code_commits_by_project
   * WHERE project_slug='{project_slug}' AND IS_COMMIT_LAST_12MO = TRUE
   */
  getSummaryData(params: MetricChartData): Observable<{columns: Column[], data: DataRow[]}> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<{success: boolean; message?: string; data: CommitActivitiesSummaryData}>(
          `${this.getEndpoint()}/summary`,
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch commit activities summary');
            }

            // Define columns for summary table
            const summaryColumns: Column[] = [
              { name: 'Definition', prop: 'definition' },
              { name: 'Total count for this project', prop: 'totalCount' },
              { name: 'Data source', prop: 'dataSource' }
            ];

            // Transform API response to summary table format
            const summaryData: DataRow[] = [
              {
                definition: {
                  text: `Commit activities are counted in GitHub, GitLab, Git, or Gerrit. View all contribution definitions in <a href="https://insights.linuxfoundation.org/docs/introduction/data-sources/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.`,
                  isHtml: true
                },
                totalCount: { text: response.data.totalCommits.toLocaleString('en-US') },
                dataSource: { text: formatDataSources(response.data.dataSources) }
              }
            ];

            return { columns: summaryColumns, data: summaryData };
          }),
          catchError(error => {
            console.error('Error fetching commit activities summary:', error);
            // Return fallback data on error
            const fallbackColumns: Column[] = [
              { name: 'Definition', prop: 'definition' },
              { name: 'Total count for this project', prop: 'totalCount' },
              { name: 'Data source', prop: 'dataSource' }
            ];
            const fallbackData: DataRow[] = [
              {
                definition: {
                  text: `Commit activities are counted in GitHub, GitLab, Git, or Gerrit. View all contribution definitions in <a href="https://insights.linuxfoundation.org/docs/introduction/data-sources/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.`,
                  isHtml: true
                },
                totalCount: { text: 'N/A' },
                dataSource: { text: 'N/A' }
              }
            ];
            return of({ columns: fallbackColumns, data: fallbackData });
          })
        );
      }),
      catchError(error => {
        console.error('Error in commit activities summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = [
          { name: 'Definition', prop: 'definition' },
          { name: 'Total count for this project', prop: 'totalCount' },
          { name: 'Data source', prop: 'dataSource' }
        ];
        const fallbackData: DataRow[] = [
          {
            definition: {
              text: `Commit activities are counted in GitHub, GitLab, Git, or Gerrit. View all contribution definitions in <a href="https://insights.linuxfoundation.org/docs/introduction/data-sources/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.`,
              isHtml: true
            },
            totalCount: { text: 'N/A' },
            dataSource: { text: 'N/A' }
          }
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }
}
